import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Send, Users, Shield, UserCheck, Loader2 } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';

export default function AdminBulkEmailPage() {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [audience, setAudience] = useState('all');
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState<{ total: number; sent: number; failed: number } | null>(null);

  const { data: users } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => fetch('/api/admin/users', { credentials: 'include' }).then(r => r.json()).then(d => d.data || []),
  });

  const getAudienceCount = () => {
    if (!users) return 0;
    switch (audience) {
      case 'all': return users.filter((u: any) => u.emailVerified).length;
      case 'verified': return users.filter((u: any) => u.emailVerified).length;
      case 'students': return users.filter((u: any) => u.role === 'student' && u.emailVerified).length;
      case 'admins': return users.filter((u: any) => u.role === 'admin').length;
      default: return 0;
    }
  };

  const handleSend = async () => {
    if (!subject.trim() || !message.trim()) {
      toast({ title: 'Error', description: 'Subject and message are required', variant: 'destructive' });
      return;
    }
    const count = getAudienceCount();
    if (!confirm(`Send this email to ${count} user${count !== 1 ? 's' : ''}?`)) return;

    setSending(true);
    setResults(null);
    try {
      const res = await fetch('/api/admin/bulk-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ subject, message, audience }),
      });
      const data = await res.json();
      if (data.success) {
        setResults(data.data);
        toast({ title: 'Success', description: `Sent to ${data.data.sent} user${data.data.sent !== 1 ? 's' : ''}` });
        setSubject('');
        setMessage('');
      } else {
        toast({ title: 'Error', description: data.error, variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Error', description: 'Failed to send bulk email', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const audiences = [
    { value: 'all', label: 'All Verified Users', icon: Users, desc: 'Everyone with a verified account' },
    { value: 'students', label: 'Students Only', icon: UserCheck, desc: 'Verified students only' },
    { value: 'admins', label: 'Admins Only', icon: Shield, desc: 'Admin users only' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Bulk Email</h1>
          <p className="text-muted-foreground">Send announcements to your users</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Audience</label>
                <div className="grid grid-cols-3 gap-2">
                  {audiences.map((a) => (
                    <button
                      key={a.value}
                      onClick={() => setAudience(a.value)}
                      className={`
                        p-3 rounded-lg border text-left transition ${
                          audience === a.value
                            ? 'border-[#B8943A] bg-[#FFF8E7]'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <a.icon className={`h-4 w-4 ${audience === a.value ? 'text-[#B8943A]' : 'text-gray-400'}`} />
                        <span className="text-sm font-medium">{a.label}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{a.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Subject</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="e.g. New Course Available!"
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B8943A]/50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Message</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={"Write your announcement here...\n\nEach line becomes a paragraph in the email."}
                  rows={10}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B8943A]/50 resize-none"
                />
                <p className="text-xs text-muted-foreground mt-1">Each line break creates a new paragraph. The email will use the ITIL Shift branded template.</p>
              </div>

              <Button
                onClick={handleSend}
                disabled={sending || !subject.trim() || !message.trim()}
                className="w-full"
                style={{ background: sending ? '#ccc' : 'linear-gradient(135deg, #B8943A, #CCA94E)', color: '#2E2A26' }}
              >
                {sending ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</>
                ) : (
                  <><Send className="h-4 w-4 mr-2" /> Send to {getAudienceCount()} User{getAudienceCount() !== 1 ? 's' : ''}</>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <CardContent className="pt-6">
              <h3 className="font-semibold mb-3">Preview</h3>
              <div style={{
                background: '#FAF8F4', borderRadius: 12, overflow: 'hidden',
                border: '1px solid #E6E2DA', fontSize: 12,
              }}>
                <div style={{ background: 'linear-gradient(135deg, #2E2A26, #3D3834)', padding: '16px 20px', textAlign: 'center' }}>
                  <div style={{ color: '#FAF8F4', fontWeight: 700, fontSize: 11, letterSpacing: 2 }}>ITIL SHIFT</div>
                  <div style={{ color: 'rgba(250,248,244,0.5)', fontSize: 9 }}>Certification Academy</div>
                </div>
                <div style={{ height: 2, background: 'linear-gradient(90deg, #B8943A, #CCA94E, #B8943A)' }} />
                <div style={{ padding: '16px 20px' }}>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>
                    {subject || 'Your Subject Here'}
                  </div>
                  <div style={{ color: '#6B6560', lineHeight: 1.6 }}>
                    <p>Hello [Name],</p>
                    {message ? message.split('\n').filter((l: string) => l.trim()).map((line: string, i: number) => (
                      <p key={i} style={{ margin: '6px 0' }}>{line}</p>
                    )) : <p style={{ fontStyle: 'italic', color: '#9E9890' }}>Your message preview...</p>}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {results && (
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold mb-3">Results</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Total recipients</span>
                    <span className="font-medium">{results.total}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Sent successfully</span>
                    <span className="font-medium text-green-600">{results.sent}</span>
                  </div>
                  {results.failed > 0 && (
                    <div className="flex justify-between text-sm">
                      <span>Failed</span>
                      <span className="font-medium text-red-600">{results.failed}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
